import { GetterTree } from 'vuex';
import { State } from '@/stores/user/state';
import { Permissions } from '@/models/permission-constants';
import { User } from '@/models/user';
import { Customer } from '@/models/customer';

export class Getters implements GetterTree<State, any> {
    [key: string]: (state: State, getters: any, rootState: any, rootGetters: any) => any;

    public user = (state: State) => state.user;
    public customers = (state: State) => {
        if (state.user === null) {
            return [];
        }
        return state.user.customers;
    }
    public hasCustomers = (state: State) => {
        return this.customers(state).length > 0;
    }
    public hasSites = (state: State) => {
        for (const customer of this.customers(state)) {
            if (customer.sites.length > 0) {
                return true;
            }
        }
        return false;
    }
    public hasAccess = (state: State, getters: any, rootState: any, rootGetters: any) => {
        return this.isAdmin(state, getters, rootState, rootGetters) || (this.hasCustomers(state) && this.hasSites(state));
    }
    public roles = (state: State) => {
        if (state.user === null) {
            return [];
        }
        return state.user.roles;
    }
    public currentCustomer = (state: State, getters: any, rootState: any, rootGetters: any) => {
        const customers = rootGetters['customer/customers'] as Array<Customer>;
        return customers.find((c) => c.id === state.currentCustomerId) || null;
    }
    public currentSite = (state: State, getters: any, rootState: any, rootGetters: any) => {
        const currentCustomer = this.currentCustomer(state, getters, rootState, rootGetters);

        if (currentCustomer === null) {
            return null;
        }

        return currentCustomer.sites.find((s) => s.id === state.currentSiteId) || null;
    }

    // Note(Seb): Permissions for the currently selected site
    public currentSitePermissions = (state: State, getters: any, rootState: any, rootGetters: any) => {
        if (this.currentSite(state, getters, rootState, rootGetters) === null) {
            return [];
        }

        const userCustomer = this.customers(state).find((c) => c.customerId === state.currentCustomerId) || null;

        if (userCustomer == null) {
            return [];
        }

        return userCustomer.sites.find((s) => s.siteId === state.currentSiteId)?.permissions || [];
    }

    // All permissions
    public hasPermission = (state: State, getters: any, rootState: any, rootGetters: any) => (permission: string) => {
        return this.hasRole(state)(permission) || this.currentSitePermissions(state, getters, rootState, rootGetters).findIndex((p) => p.name === permission) > -1;
    }
    public hasAnyPermission = (state: State, getters: any, rootState: any, rootGetters: any) => (permissions: Array<string>) => {
        return this.hasAnyRole(state)(permissions) || this.currentSitePermissions(state, getters, rootState, rootGetters).some((p) => permissions.indexOf(p.name) > -1);
    }
    public hasAnyPermissionId = (state: State, getters: any, rootState: any, rootGetters: any) => (permissionIds: Array<string>) => {
        return this.hasAnyRoleId(state)(permissionIds) || this.currentSitePermissions(state, getters, rootState, rootGetters).some((p) => permissionIds.indexOf(p.permissionId!) > -1);
    }

    // User roles
    public hasRole = (state: State) => (role: string) => {
        return this.roles(state).findIndex((r) => r.name === role) > -1;
    }
    public hasAnyRole = (state: State) => (roles: Array<string>) => {
        return this.roles(state).some((r) => roles.indexOf(r.name) > -1);
    }
    public hasAnyRoleId = (state: State) => (rolesIds: Array<string>) => {
        return this.roles(state).some((r) => rolesIds.indexOf(r.roleId!) > -1);
    }

    public isSystem = (state: State, getters: any, rootState: any, rootGetters: any): boolean => this.hasAnyPermission(state, getters, rootState, rootGetters)([ Permissions.SYSTEM ]);
    public isAdmin = (state: State, getters: any, rootState: any, rootGetters: any): boolean => this.hasAnyPermission(state, getters, rootState, rootGetters)([ Permissions.SYSTEM, Permissions.ADMIN ]);
    public canViewEnquiries = (state: State, getters: any, rootState: any, rootGetters: any): boolean => this.hasAnyPermission(state, getters, rootState, rootGetters)([ Permissions.SYSTEM, Permissions.ADMIN, Permissions.VIEW_ENQUIRIES ]);
    public canCreateEnquiries = (state: State, getters: any, rootState: any, rootGetters: any): boolean => this.hasAnyPermission(state, getters, rootState, rootGetters)([ Permissions.SYSTEM, Permissions.ADMIN, Permissions.CREATE_ENQUIRIES ]);
    public canViewQuotes = (state: State, getters: any, rootState: any, rootGetters: any): boolean => this.hasAnyPermission(state, getters, rootState, rootGetters)([ Permissions.SYSTEM, Permissions.ADMIN, Permissions.VIEW_QUOTES ]);
    public canCreateQuotes = (state: State, getters: any, rootState: any, rootGetters: any): boolean => this.hasAnyPermission(state, getters, rootState, rootGetters)([ Permissions.SYSTEM, Permissions.ADMIN, Permissions.CREATE_QUOTES ]);
    public canViewOrders = (state: State, getters: any, rootState: any, rootGetters: any): boolean => this.hasAnyPermission(state, getters, rootState, rootGetters)([ Permissions.SYSTEM, Permissions.ADMIN, Permissions.VIEW_ORDERS ]);
    public canCreateOrders = (state: State, getters: any, rootState: any, rootGetters: any): boolean => this.hasAnyPermission(state, getters, rootState, rootGetters)([ Permissions.SYSTEM, Permissions.ADMIN, Permissions.CREATE_ORDERS ]);
    public canViewApprovals = (state: State, getters: any, rootState: any, rootGetters: any): boolean => this.hasAnyPermission(state, getters, rootState, rootGetters)([ Permissions.SYSTEM, Permissions.ADMIN, Permissions.VIEW_APPROVALS ]);
    public canApproveUpto1K = (state: State, getters: any, rootState: any, rootGetters: any): boolean => this.hasAnyPermission(state, getters, rootState, rootGetters)([ Permissions.SYSTEM, Permissions.ADMIN, Permissions.APPROVE_UP_TO_1K ]);
    public canApproveFrom1KTo5K = (state: State, getters: any, rootState: any, rootGetters: any): boolean => this.hasAnyPermission(state, getters, rootState, rootGetters)([ Permissions.SYSTEM, Permissions.ADMIN, Permissions.APPROVE_FROM_1K_TO_5K ]);
    public canApproveFrom5KTo10K = (state: State, getters: any, rootState: any, rootGetters: any): boolean => this.hasAnyPermission(state, getters, rootState, rootGetters)([ Permissions.SYSTEM, Permissions.ADMIN, Permissions.APPROVE_FROM_5K_TO_10K ]);
    public canApproveAbove10K = (state: State, getters: any, rootState: any, rootGetters: any): boolean => this.hasAnyPermission(state, getters, rootState, rootGetters)([ Permissions.SYSTEM, Permissions.ADMIN, Permissions.APPROVE_ALL ]);

    public userIsSystem = () => (user: User) => {
        return user.roles.some((r) => r.name === Permissions.SYSTEM);
    }
    public userIsAdmin = () => (user: User) => {
        return user.roles.some((r) => r.name === Permissions.SYSTEM || r.name === Permissions.ADMIN);
    }
}
